import { FormattedMessage, useIntl } from '@umijs/max';
import { theme, Typography } from 'antd';
import React, { createContext, useContext, useState } from 'react';

const { Text } = Typography;

interface DownloadLoadingContextType {
  isDownloading: boolean;
  setIsDownloading: (loading: boolean) => void;
}

const DownloadLoadingContext = createContext<DownloadLoadingContextType>({
  isDownloading: false,
  setIsDownloading: () => {},
});

export const useDownloadLoading = () => useContext(DownloadLoadingContext);

export const DownloadLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { token } = theme.useToken();
  const intl = useIntl();

  React.useEffect(() => {
    const handleDownloadStart = () => setIsDownloading(true);
    const handleDownloadEnd = () => setIsDownloading(false);

    window.addEventListener('download-start', handleDownloadStart);
    window.addEventListener('download-end', handleDownloadEnd);

    return () => {
      window.removeEventListener('download-start', handleDownloadStart);
      window.removeEventListener('download-end', handleDownloadEnd);
    };
  }, []);

  return (
    <DownloadLoadingContext.Provider value={{ isDownloading, setIsDownloading }}>
      {children}
      {isDownloading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 8,
              padding: '32px 48px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <img
              src="/loading.svg"
              alt={intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading...' })}
              style={{ width: 100, height: 100 }}
            />
            <Text style={{ fontSize: 16, color: token.colorTextSecondary }}>
              <FormattedMessage
                id="components.downloadLoadingMask.downloading"
                defaultMessage="Downloading..."
              />
            </Text>
          </div>
        </div>
      )}
    </DownloadLoadingContext.Provider>
  );
};
